import type {CommonProps} from '@pinecast/common/icons/types';
import type {CSS} from '@pinecast/styles';
import styled, {focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';

const sizes = {
  normal: 30,
  small: 24,
  large: 36,
};

const NativeButton = styled(
  'button',
  (
    {
      disabled,
      $isBlock,
      $isOn,
      $size,
      $style,
    }: {
      disabled?: boolean;
      $isBlock?: boolean;
      $isOn?: boolean;
      $size: 'normal' | 'large' | 'small';
      $style?: CSS;
    },
    designColors,
  ) => {
    const shadow = $isOn
      ? `inset 0 0 0 2px ${designColors.contentText}`
      : '0 0 0 transparent inset';
    return {
      alignItems: 'center',
      backgroundColor: 'transparent',
      border: 0,
      borderRadius: 3,
      boxShadow: shadow,
      color: designColors.contentText,
      cursor: 'pointer',
      display: $isBlock ? 'flex' : 'inline-flex',
      height: sizes[$size],
      justifyContent: 'center',
      marginTop: 0,
      marginRight: 0,
      marginBottom: $isBlock ? 20 : 0,
      marginLeft: 0,
      minWidth: 20,
      opacity: disabled ? 0.5 : 1,
      padding: '0 4px',
      pointerEvents: disabled ? 'none' : undefined,
      transition: 'background-color 0.2s, box-shadow 0.2s, opacity 0.2s',

      ...$style,

      ':hover': {
        boxShadow: shadow,
      },
      ':active': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        boxShadow: shadow,
      },
      // Against the edge rather than 2px out: icon buttons sit in tight spots,
      // such as the end cap of a field, where a wider ring would be cut off.
      // The button has no fill of its own, so the ring still meets the surface.
      //
      // A button that can be on is the exception. Its on state is a 2px border
      // just inside the edge, in the ring's color, so a ring on the edge would
      // only make that border thicker. 2px out, the surface shows between them.
      ...focusVisibleRing(designColors, $isOn === undefined ? 0 : 2),
    };
  },
  {className: 'IconButton-nativeButton'},
);

const IconButton = React.forwardRef(
  (
    {
      className,
      Component,
      iconProps,
      onClick,
      size = 'normal',
      style,
      ...rest
    }: Omit<
      React.AllHTMLAttributes<HTMLButtonElement>,
      'size' | 'onClick' | 'style'
    > & {
      Component: React.ComponentType<CommonProps>;
      iconProps?: Partial<CommonProps>;
      $isBlock?: boolean;
      /**
       * Whether the button is on, for a button that can be: it draws a 2px
       * border inside its edge. Screen readers do not see it, so a toggle
       * also sets `aria-pressed`.
       */
      $isOn?: boolean;
      onClick: () => void;
      size?: 'normal' | 'small' | 'large';
      style?: CSS;
    },
    ref,
  ) => (
    <NativeButton
      aria-label={rest.title}
      className={className}
      $size={size}
      $style={style}
      onClick={(e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.preventDefault();
        onClick();
      }}
      type="button"
      {...rest}
      ref={ref}
    >
      <Component {...iconProps} />
    </NativeButton>
  ),
);

export default IconButton;
